import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import {
  STANDARD_PIPELINE_TYPE,
  type CampaignPipelineDefinition,
  type PipelineMilestone,
  type PipelineStageDefinition,
} from "@/marketing/models/campaignPipeline";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function selectPipelineDefinition(
  definitions: CampaignPipelineDefinition[],
  pipelineType?: string,
): CampaignPipelineDefinition | undefined {
  const type = pipelineType ?? STANDARD_PIPELINE_TYPE;
  return definitions.find((definition) => definition.type === type);
}

export function getStageDefinition(
  definition: CampaignPipelineDefinition,
  stage: string,
): PipelineStageDefinition | undefined {
  return definition.stages.find((s) => s.stage === stage);
}

export function getStageLabel(
  definition: CampaignPipelineDefinition,
  stage: string,
): string {
  return getStageDefinition(definition, stage)?.labels.admin ?? stage;
}

export function hasMilestone(
  definition: CampaignPipelineDefinition,
  stage: string,
  milestone: PipelineMilestone,
): boolean {
  return (
    getStageDefinition(definition, stage)?.milestones.includes(milestone) ??
    false
  );
}

// Rejected creators never count toward a milestone.
export function countByMilestone(
  definition: CampaignPipelineDefinition,
  creators: CampaignInfluencer[],
  milestone: PipelineMilestone,
): number {
  return creators.filter(
    (c) => !c.rejected && hasMilestone(definition, c.pipelineStage, milestone),
  ).length;
}

export interface TransitionCheck {
  allowed: boolean;
  requiresFastTrack: boolean;
}

export function canTransition(
  definition: CampaignPipelineDefinition,
  fromStage: string,
  toStage: string,
): TransitionCheck {
  const denied: TransitionCheck = { allowed: false, requiresFastTrack: false };
  const { allowBackward, allowSkipWithFastTrack } = definition.transitionPolicy;
  const fromIndex = definition.stages.findIndex((s) => s.stage === fromStage);
  const toIndex = definition.stages.findIndex((s) => s.stage === toStage);

  if (toIndex < 0 || fromStage === toStage) return denied;
  // A creator stuck in a stage the pipeline no longer has can always be moved back into it.
  if (fromIndex < 0) return { allowed: true, requiresFastTrack: false };
  if (toIndex < fromIndex) {
    return allowBackward ? { allowed: true, requiresFastTrack: false } : denied;
  }
  if (toIndex === fromIndex + 1) {
    return { allowed: true, requiresFastTrack: false };
  }
  return allowSkipWithFastTrack
    ? { allowed: true, requiresFastTrack: true }
    : denied;
}

export interface BoardColumn {
  stage: PipelineStageDefinition;
  creators: CampaignInfluencer[];
}

export interface BoardColumns {
  columns: BoardColumn[];
  // Creators whose stage is not part of the pipeline definition.
  other: CampaignInfluencer[];
  rejected: CampaignInfluencer[];
}

export function deriveBoardColumns(
  definition: CampaignPipelineDefinition,
  creators: CampaignInfluencer[],
): BoardColumns {
  const columns: BoardColumn[] = definition.stages.map((stage) => ({
    stage,
    creators: [],
  }));
  const columnByStage = new Map(columns.map((c) => [c.stage.stage, c]));
  const other: CampaignInfluencer[] = [];
  const rejected: CampaignInfluencer[] = [];

  for (const creator of creators) {
    if (creator.rejected) rejected.push(creator);
    else
      (columnByStage.get(creator.pipelineStage)?.creators ?? other).push(
        creator,
      );
  }
  return { columns, other, rejected };
}

export interface AdminTurnSummary {
  waitingOnAdmin: number;
  overdue: number;
}

export function summarizeAdminTurn(
  definition: CampaignPipelineDefinition,
  creators: CampaignInfluencer[],
  now: Date = new Date(),
): AdminTurnSummary {
  let waitingOnAdmin = 0;
  let overdue = 0;
  for (const creator of creators) {
    if (creator.rejected) continue;
    const stage = getStageDefinition(definition, creator.pipelineStage);
    if (stage?.waitingOn !== "admin") continue;
    waitingOnAdmin += 1;
    const overdueAfterDays = stage.adminTask?.overdueAfterDays;
    if (overdueAfterDays === undefined) continue;
    const since = new Date(creator.lastTransitionAt ?? creator.joinedAt);
    if (now.getTime() - since.getTime() > overdueAfterDays * MS_PER_DAY) {
      overdue += 1;
    }
  }
  return { waitingOnAdmin, overdue };
}
